import {AxiosError} from 'axios'
import moment from 'moment'

import clientSDK from '../../../App/ClientSDK'
import {axiosErrorHandler} from '../../../App/Util/axiosErrorHandler'
import {TCalendarDateTemplateInfo} from '../../../App/Util/PrimeCalendarUtil/DateTemplate'
import {IAbsenceNew, IAPIAbsenceNew, IAPIAbsenceNewResponse,} from '../IAbsenceNew'

interface ICreateAbsence {
  starts_at: string
  ends_at: string
  absence_type_uuid: string
  person_uuid: string
  reason: string
}

interface IDeleteAbsenceSuccess {
  status: 'success'
}

const baseUrl = 'hr/absences'

const absenceApiTransformer = (data: IAPIAbsenceNew): IAbsenceNew => ({
  uuid: data.uuid,
  startsAt: moment(data.starts_at),
  endsAt: moment(data.ends_at),
  absenceType: {
    uuid: data.absence_type.uuid,
    key: data.absence_type.key,
    color: data.absence_type.color,
    shouldBeCaughtUp: data.absence_type.should_be_caught_up,
  },
  isGeneralAbsence: data.is_general_absence,
  accruedTime: data.accrued_time,
  reason: data.reason,
  person: {
    id: data.person.id,
    uuid: data.person.uuid,
  },
})


const absenceForCalendarTransformer = (data: IAPIAbsenceNew): TCalendarDateTemplateInfo => ({
  tooltip: `absence.${data.absence_type.key}`, 
  startsAt: moment(data.starts_at), 
  endsAt: moment(data.ends_at), 
  color: data.absence_type.color, 
  isGeneralAbsence: data.is_general_absence,
  uuid: data.uuid,
  personUuid: data.person.uuid
})


const getList = async () => {
  const response = await clientSDK
    .instance()
    .get<IAPIAbsenceNewResponse>(baseUrl)
  const data = response.data
  return data._collection.map(absence => absenceApiTransformer(absence))
}

const getCalendarData = async (startsAt: string, endsAt: string): Promise<TCalendarDateTemplateInfo[]> => {
  const response = await clientSDK
    .instance()
    .get<IAPIAbsenceNewResponse>(
      baseUrl,
     { starts_at: startsAt, ends_at: endsAt })
  const data = response.data
  return data._collection.map(absence => absenceForCalendarTransformer(absence))
}

const getAbsence = async (absenceUuid: string) => {
  const response = await clientSDK
    .instance()
    .get<{ _resource: IAPIAbsenceNew }>(`${baseUrl}/${absenceUuid}`)
  const data = response.data._resource
  return absenceApiTransformer(data)
}

const createAbsence = async (generalAbsence: ICreateAbsence) => {
  try {
    return clientSDK.instance().post<ICreateAbsence>(baseUrl, generalAbsence)
  } catch (error) {
    const handleAxiosError = axiosErrorHandler<any>(res => res)
    return handleAxiosError(error as Error | AxiosError)
  }
}

const deleteAbsence = async (absenceUuid: string) => {
  return clientSDK
    .instance()
    .delete<IDeleteAbsenceSuccess>(`${baseUrl}/${absenceUuid}`)
}

const updateAbsence = async (generalAbsenceObject: {
  generalAbsence: ICreateAbsence
  absenceUuid: string
}) => {
  const { generalAbsence, absenceUuid } = generalAbsenceObject
  const response = await clientSDK
    .instance()
    .put<ICreateAbsence>(`${baseUrl}/${absenceUuid}`, generalAbsence)
  return response.data
}

const useAbsencesService = {
  getList,
  getAbsence,
  createAbsence,
  deleteAbsence,
  updateAbsence,
  getCalendarData
}

export default useAbsencesService
